import { useCallback, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Dot, NakedButton, useModalControl } from '@frontend/design-system';
import { ServiceSelector } from '../../../components/service-selector/service-selector';
import { AvailabilityDateNavigator } from '../../BookingSite/RequestAppointment/ProviderSelect/availability-date-navigator';
import type { AvailabilityHeaderFieldProps } from './availability-header.types';
import { ChecklistPopover } from './checklist-popover';
import { FiltersTray } from './filters-tray';

type AvailabilityHeaderProps = AvailabilityHeaderFieldProps & {
  locationId: string;
  orgId: string;
};

export const AvailabilityHeader = ({
  locationId,
  orgId,
  weekInView,
  weekDatePicker,
  handleDateChange,
  goToNextWeek,
  goToPreviousWeek,
  goToThisWeek,
  isCurrentWeek,
  selectedServiceId,
  onServiceSelect,
  isMultiLocation,
  locationOptions,
  locationChecklistField,
  timeRangeOptions,
  timeRangeChecklistField,
  operatories,
  operatoryChecklistField,
  providers,
  providerChecklistField,
}: AvailabilityHeaderProps) => {
  const { t } = useTranslation('schedule');
  const containerRef = useRef<HTMLElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const updateWidth = useCallback((entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      setContainerWidth(entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width);
    }
  }, []);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    setContainerWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [updateWidth]);
  const isMedium = containerWidth > 0 && containerWidth <= 1160;
  const isSmall = containerWidth > 0 && containerWidth <= 480;
  const { modalProps, triggerProps, openModal } = useModalControl();

  const hasActiveFilters =
    (isMultiLocation && locationOptions.length > 1 && locationChecklistField.value.length > 0) ||
    timeRangeChecklistField.value.length > 0 ||
    operatoryChecklistField.value.length > 0 ||
    providerChecklistField.value.length > 0;

  return (
    <section css={styles.container} ref={containerRef}>
      <section css={styles.dateSelector}>
        <Button
          variant='secondary'
          size='small'
          disabled={isCurrentWeek}
          onClick={goToThisWeek}
          iconName={isSmall ? 'calendar-follow-up' : undefined}
          css={isSmall ? { border: `1px solid ${theme.colors.neutral20}` } : undefined}
        >
          {!isSmall && t('Today')}
        </Button>
        <Button
          variant='secondary'
          size='small'
          iconName='caret-left'
          disabled={isCurrentWeek}
          onClick={goToPreviousWeek}
        />
        <AvailabilityDateNavigator
          key={weekInView}
          weekInView={weekInView}
          minDate={dayjs().format('MM/DD/YYYY')}
          handleDateChange={handleDateChange}
          dateFieldProps={weekDatePicker}
          isSchedularV3Enabled={true}
          selectedLocationTimeZone=''
        />
        <Button variant='secondary' size='small' iconName='caret-right' onClick={goToNextWeek} />
      </section>
      <section css={styles.filters}>
        <ServiceSelector
          locationId={locationId}
          orgId={orgId}
          selectedId={selectedServiceId}
          placement='bottom-start'
          onSelect={onServiceSelect}
        />
        {isMedium ? (
          <>
            <NakedButton
              {...triggerProps}
              css={styles.filterIconButton}
              aria-label={t('Open filters')}
              onClick={openModal}
            >
              <Icon name='filter-alt' size={16} color='default' />
              {hasActiveFilters && (
                <Dot
                  color='primary'
                  css={{
                    width: 8,
                    height: 8,
                    top: 6,
                    left: 22,
                    border: `1px solid ${theme.colors.white}`,
                  }}
                />
              )}
            </NakedButton>
            <FiltersTray
              modalProps={modalProps}
              isMultiLocation={isMultiLocation}
              locationOptions={locationOptions}
              locationChecklistField={locationChecklistField}
              timeRangeOptions={timeRangeOptions}
              timeRangeChecklistField={timeRangeChecklistField}
              operatories={operatories}
              operatoryChecklistField={operatoryChecklistField}
              providers={providers}
              providerChecklistField={providerChecklistField}
            />
          </>
        ) : (
          <>
            {isMultiLocation && locationOptions.length > 1 && (
              <ChecklistPopover
                label={t('Location')}
                fieldName='locationIds'
                leadingIcon='location'
                options={locationOptions}
                checklistField={locationChecklistField}
              />
            )}
            <ChecklistPopover
              label={t('Time of Day')}
              fieldName='timeRanges'
              leadingIcon='clock'
              options={timeRangeOptions}
              checklistField={timeRangeChecklistField}
            />
            {operatories.length > 0 && (
              <ChecklistPopover
                label={t('Operatory')}
                fieldName='operatoryIds'
                leadingIcon='department'
                options={operatories}
                checklistField={operatoryChecklistField}
              />
            )}
            {providers.length > 0 && (
              <ChecklistPopover
                label={t('Provider')}
                fieldName='providerIds'
                leadingIcon='user'
                options={providers}
                checklistField={providerChecklistField}
              />
            )}
          </>
        )}
      </section>
    </section>
  );
};

const styles = {
  container: css`
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(1.5)};
    background-color: ${theme.colors.white};
    border-top: 1px solid ${theme.colors.neutral20};
    border-bottom: 1px solid ${theme.colors.neutral20};
    width: 100%;
  `,
  filters: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
  `,
  filterIconButton: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.neutral20};
    background-color: ${theme.colors.white};
    min-width: 40px;
    height: 32px;
    box-sizing: border-box;
  `,
  dateSelector: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
};
